
import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { ApprovalRequest } from "../types/legalOps";
import { 
  deleteApprovalRequest
} from "../services/approvalRequestsService";
import { db, doc, getDoc } from "../../firebaseClient";
import { useToast } from "../components/Toast";
import { useAiContext } from "../ai/AiContext";
import { isSuperUser, canInitiateLegalRequest } from "../config/roles";
import { SignRequestDetailModal } from "../modules/sign/components/SignRequestDetailModal";
import { SealRequestFormModal } from "../modules/seal/components/SealRequestFormModal";
import { exportToExcel } from "../export/excel";

// Strict Terminal Statuses for History Tab
const HISTORY_STATUSES = [
  'APPROVED', 'FULLY_APPROVED', 'REJECTED', 'RETURNED', 
  'LEGAL_RETURNED', 'PUBLISHED', 'COMPLETED', 'CANCELLED'
];

export function SealApplicationPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<'PENDING' | 'HISTORY'>('PENDING');
  const [items, setItems] = useState<ApprovalRequest[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Modal States
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  
  // Permission Check
  const canCreate = user ? canInitiateLegalRequest(user.role) : false;
  
  // Data Fetching: Dual Path + Approval Flow Truth Check
  const refreshData = async () => {
      setLoading(true);
      try {
          const allItemsMap = new Map<string, ApprovalRequest>();

          // 1. Fetch from 'approval_requests' (module=SEAL_REQUEST) - New Standard
          const q1 = db.collection("approval_requests").where("module", "==", "SEAL_REQUEST");
          const snap1 = await q1.get();
          snap1.docs.forEach(doc => {
              const d = { id: doc.id, ...doc.data() } as ApprovalRequest;
              allItemsMap.set(d.id, d);
          });
          
          // 2. Fetch from 'approval_requests' (module=sealRequest) - Legacy Compat
          const q2 = db.collection("approval_requests").where("module", "==", "sealRequest");
          const snap2 = await q2.get();
          snap2.docs.forEach(doc => {
              if (!allItemsMap.has(doc.id)) {
                 const d = { id: doc.id, ...doc.data() } as ApprovalRequest;
                 allItemsMap.set(d.id, d);
              }
          });

          // 3. (Optional) Legacy 'seal_requests' collection - Read Only
          const q3 = db.collection("seal_requests");
          const snap3 = await q3.get();
          snap3.docs.forEach(doc => {
              const d = doc.data();
              if (!allItemsMap.has(doc.id)) {
                  const mapped: ApprovalRequest = {
                      id: doc.id,
                      module: "SEAL_REQUEST",
                      title: d.title || d.docNo,
                      status: d.status || "DRAFT",
                      docCode: d.docNo || d.docCode,
                      requesterName: d.applicant || d.requesterName,
                      department: d.department || d.applicantDept,
                      sealType: d.sealType,
                      copyCount: d.copyCount,
                      description: d.description || d.summary,
                      submittedAt: d.createdAt, 
                      createdAt: d.createdAt,
                      updatedAt: d.updatedAt,
                      priority: 'normal',
                      sensitivity: 'normal',
                      requesterUid: d.applicantId || '',
                      attachments: d.attachments || []
                  } as any;
                  allItemsMap.set(doc.id, mapped);
              }
          });
          
          const rawList = Array.from(allItemsMap.values());

          // 4. [CRITICAL FIX] Attach Effective Status from Flow
          // We must check the actual Approval Flow status because the parent doc status might be stuck.
          const enrichedList = await Promise.all(rawList.map(async (item) => {
              // Skip draft/cancelled, they don't have flows usually
              if (item.status === 'DRAFT' || item.status === 'CANCELLED') return item;

              try {
                  // Check both flow ID conventions
                  let flowDoc = await getDoc(doc(db, "approvalFlows", `SEAL_REQUEST_${item.id}`));
                  if (!flowDoc.exists) {
                      flowDoc = await getDoc(doc(db, "approvalFlows", `sealRequest_${item.id}`));
                  }
                  
                  if (flowDoc.exists) {
                      const flowData = flowDoc.data();
                      const fStatus = flowData?.status; // IN_PROGRESS, COMPLETED, REJECTED
                      const stages = flowData?.stages || [];
                      
                      // Derive effective status from stages if flow status is vague
                      let effectiveStatus = item.status;

                      if (fStatus === 'REJECTED') {
                          effectiveStatus = 'REJECTED';
                      } else if (fStatus === 'COMPLETED') {
                          effectiveStatus = 'FULLY_APPROVED';
                      } else {
                          // Check if any stage is REJECTED/RETURNED which implies flow stoppage
                          if (stages.some((s:any) => s.status === 'REJECTED')) effectiveStatus = 'REJECTED';
                          else if (stages.some((s:any) => s.status === 'RETURNED')) effectiveStatus = 'RETURNED';
                      }
                      
                      if (effectiveStatus !== item.status) {
                          return { ...item, status: effectiveStatus };
                      }
                  }
              } catch (e) {
                  console.warn(`Failed to check flow for ${item.id}`, e);
              }
              return item;
          }));
          
          // Sort by Date Descending
          enrichedList.sort((a, b) => {
             const tA = a.updatedAt?.toMillis ? a.updatedAt.toMillis() : 0;
             const tB = b.updatedAt?.toMillis ? b.updatedAt.toMillis() : 0;
             return tB - tA;
          });

          setItems(enrichedList);
      } catch (e) {
          console.error("Fetch Error:", e);
      } finally {
          setLoading(false);
      }
  };

  useEffect(() => { 
      refreshData(); 
  }, [user]);

  // strict filtering based on corrected status
  const pendingItems = items.filter(i => !HISTORY_STATUSES.includes(i.status));
  const historyItems = items.filter(i => HISTORY_STATUSES.includes(i.status));

  const handleExportHistory = () => {
      if (historyItems.length === 0) {
          alert("目前無歷史紀錄可匯出");
          return;
      }

      const exportData = historyItems.map(item => {
          const submittedAt = item.submittedAt?.toDate 
            ? item.submittedAt.toDate().toLocaleString() 
            : (item.submittedAt || "");
            
          const updatedAt = item.updatedAt?.toDate 
            ? item.updatedAt.toDate().toLocaleString() 
            : (item.updatedAt || "");

          return {
              "單號": item.docCode || item.id,
              "文件標題": item.title,
              "申請人": item.requesterName,
              "部門": item.department,
              "印鑑類型": item.sealType || "-",
              "份數": item.copyCount || 1,
              "目前狀態": item.status,
              "送出時間": submittedAt,
              "最後更新": updatedAt
          };
      });

      exportToExcel(`Seal_History_${new Date().toISOString().slice(0, 10)}`, exportData);
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header / Toolbar */}
      <div className="row" style={{ borderBottom: '1px solid #333', marginBottom: 16, justifyContent: 'space-between', alignItems: 'center', paddingBottom: 8 }}>
        <div className="row" style={{gap: 12}}>
            <button 
                className="navbtn" 
                style={{
                    width: 'auto', 
                    borderBottom: activeTab === 'PENDING' ? '2px solid #fff' : '2px solid transparent', 
                    color: activeTab === 'PENDING' ? '#fff' : 'var(--muted)',
                    fontWeight: activeTab === 'PENDING' ? 'bold' : 'normal'
                }} 
                onClick={() => setActiveTab('PENDING')}
            >
                進行中 ({pendingItems.length})
            </button>
            <button 
                className="navbtn" 
                style={{
                    width: 'auto', 
                    borderBottom: activeTab === 'HISTORY' ? '2px solid #fff' : '2px solid transparent', 
                    color: activeTab === 'HISTORY' ? '#fff' : 'var(--muted)',
                    fontWeight: activeTab === 'HISTORY' ? 'bold' : 'normal'
                }} 
                onClick={() => setActiveTab('HISTORY')}
            >
                歷史紀錄 ({historyItems.length})
            </button>
        </div>

        {/* Action Button Group */}
        <div className="row" style={{ gap: 10 }}>
            {activeTab === 'HISTORY' && (
                <button 
                    className="smallbtn" 
                    onClick={handleExportHistory}
                    title="匯出歷史紀錄 CSV"
                >
                    📤 匯出 CSV
                </button>
            )}

            {/* Create Button only in Pending Tab */}
            {canCreate && activeTab === 'PENDING' && (
                <button 
                    className="btn" 
                    style={{ width: 'auto', padding: '6px 16px', fontSize: 13, background: '#fff', color: '#000', fontWeight: 'bold' }}
                    onClick={() => setIsCreateOpen(true)}
                >
                    ＋ 新增用印申請
                </button>
            )}
        </div>
      </div>

      <div style={{ flex: 1, overflow: 'hidden', display:'flex', flexDirection:'column' }}>
        {loading && <div className="p" style={{padding:20}}>載入中 (正在同步流程狀態)...</div>}
        {!loading && (
            activeTab === 'PENDING' ? (
                <SealList items={pendingItems} onRefresh={refreshData} />
            ) : (
                <SealList items={historyItems} onRefresh={refreshData} isHistory />
            )
        )}
      </div>

      {/* Create Modal */}
      {isCreateOpen && (
        <SealRequestFormModal 
            onClose={() => setIsCreateOpen(false)}
            onSuccess={() => {
                setIsCreateOpen(false);
                refreshData(); 
            }}
        />
      )}
    </div>
  );
}

function SealList({ items, onRefresh, isHistory }: any) {
    const [selectedItem, setSelectedItem] = useState<ApprovalRequest | null>(null);
    const { user } = useAuth();
    const isSuper = isSuperUser(user?.email);
    const { requestDelete } = useAiContext();
    const { showToast } = useToast();

    const getStatusLabel = (s: string) => {
        const map: any = {
            'IN_REVIEW': '審查中',
            'PENDING_LEGAL': '待法務審查',
            'PENDING_MANAGER': '待主管核決',
            'WAITING_APPROVAL': '待核決',
            'DRAFT': '草稿',
            'SUBMITTED': '已送出',
            'RETURNED': '退回修正',
            'APPROVED': '已核准',
            'FULLY_APPROVED': '流程完成',
            'REJECTED': '已駁回',
            'LEGAL_RETURNED': '法務退回',
            'CANCELLED': '已作廢'
        };
        return map[s] || s || "未知";
    };

    const getStatusColor = (s: string) => {
        if (['APPROVED', 'FULLY_APPROVED', 'COMPLETED'].includes(s)) return 'var(--ok)';
        if (['REJECTED', 'CANCELLED', 'LEGAL_RETURNED', 'RETURNED'].includes(s)) return 'var(--danger)';
        if (['DRAFT'].includes(s)) return '#666';
        return '#fbbf24'; // In Progress
    };

    const handleDelete = (item: ApprovalRequest) => {
        requestDelete({
            targetType: 'sealRequest',
            targetId: item.id,
            summary: item.title,
            onConfirm: async () => {
                await deleteApprovalRequest(item.id);
                showToast("已刪除");
                onRefresh();
            }
        });
    };

    return (
        <div style={{height: '100%', display: 'flex', flexDirection: 'column'}}>
            <div style={{flex:1, overflow:'auto', background:'#161616', border:'1px solid var(--border)', borderRadius:12}}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead style={{ background: '#222', color: '#aaa', position: 'sticky', top: 0 }}>
                        <tr>
                            <th style={{padding:12}}>單號</th>
                            <th style={{padding:12}}>文件名稱</th>
                            <th style={{padding:12}}>用印類型</th>
                            <th style={{padding:12}}>申請人 / 部門</th>
                            <th style={{padding:12}}>狀態</th>
                            <th style={{padding:12}}>操作</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.map((item: ApprovalRequest) => (
                            <tr key={item.id} style={{borderBottom:'1px solid #333'}}>
                                <td style={{padding:12, fontFamily:'monospace', color:'#a5b4fc'}}>{item.docCode || item.id.slice(0,8)}</td>
                                <td style={{padding:12, fontWeight:500, color:'#fff'}}>{item.title}</td>
                                <td style={{padding:12}}>
                                    <span style={{color: '#ddd', background: 'rgba(255,255,255,0.1)', padding:'2px 6px', borderRadius: 4, fontSize: 12}}>
                                        {item.sealType || "未指定"}
                                    </span>
                                </td>
                                <td style={{padding:12}}>{item.requesterName} <span style={{color:'#666', fontSize:11}}>({item.department})</span></td>
                                <td style={{padding:12}}>
                                    <span className="badge" style={{color: getStatusColor(item.status), borderColor: getStatusColor(item.status)}}>
                                        {getStatusLabel(item.status)}
                                    </span>
                                </td>
                                <td style={{padding:12}}>
                                    <button className="smallbtn" onClick={() => setSelectedItem(item)}>
                                        {isHistory ? "檢視" : "簽核/詳情"}
                                    </button>
                                    {(isSuper || item.status === 'DRAFT') && (
                                        <button className="smallbtn" style={{marginLeft:8, color:'var(--danger)', borderColor:'var(--danger)'}} onClick={() => handleDelete(item)}>
                                            刪除
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {items.length === 0 && <tr><td colSpan={6} style={{padding:40, textAlign:'center', color:'#666'}}>無資料</td></tr>}
                    </tbody>
                </table>
            </div>
            {selectedItem && (
                <SignRequestDetailModal request={selectedItem} onClose={() => setSelectedItem(null)} onUpdate={() => { setSelectedItem(null); onRefresh(); }} />
            )}
        </div>
    );
}
